import * as React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import z from 'zod';
import { type QuerySuggestion, type QuerySuggestionInput } from '@/types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RiDeleteBin6Line } from '@remixicon/react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { Button } from '../atoms/button';

export type QuerySuggestionFormModalProps = {
  isOpen: boolean;
  mode: "create" | "edit";
  onOpenChange: (open: boolean) => void;
  querySuggestion?: QuerySuggestion;
  onSubmit: (data: QuerySuggestionInput) => void;
  onDelete?: (uid: number) => void;
  isLoading?: boolean;
};

const querySuggestionFormSchema = z.object({
  suggestion: z.string().min(3, "Query suggestion is required").max(255, "Query suggestion must be less than 255 characters"),
});

export type QuerySuggestionFormData = z.infer<typeof querySuggestionFormSchema>;

export default function QuerySuggestionFormModal(props: QuerySuggestionFormModalProps) {
  const { isOpen, onOpenChange, mode, querySuggestion, onSubmit, onDelete, isLoading } = props;
  const form = useForm<QuerySuggestionFormData>({
    resolver: zodResolver(querySuggestionFormSchema),
    defaultValues: {
      suggestion: querySuggestion?.suggestion || '',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      console.log('resetting form');
      form.reset({
        suggestion: querySuggestion?.suggestion || '',
      });
    }
  }, [isOpen, querySuggestion, form]);

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (onDelete && querySuggestion) {
      onDelete(querySuggestion.uid);
    }
  };

  const handleSubmit = (data: QuerySuggestionFormData) => {
    onSubmit({
      uid: querySuggestion?.uid || 0,
      suggestion: data.suggestion,
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create New Query Suggestion" : "Edit Query Suggestion"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create" ? "Add a new query suggestion." : "Update a query suggestion."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="suggestion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Suggestion</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter query suggestion" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              {mode === "edit" && onDelete && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={isLoading}>
                  <RiDeleteBin6Line className="size-4" />
                  Delete
                </Button>
              )}
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}